import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY } from "@/data/site";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Méthode", href: "#methode" },
  { label: "Réalisations", href: "#realisations" },
  { label: "Zones", href: "#zones" },
  { label: "Avis", href: "#avis" },
];

export const Navbar = ({ onQuote }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-mh-bg/90 backdrop-blur-md border-b border-mh-line" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button onClick={() => go("#top")} className="flex flex-col leading-none group" data-testid="nav-logo">
          <span className="font-display font-black text-xl tracking-tight text-white">MH</span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-mh-orange font-bold">Étanchéité</span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-sm text-mh-slate hover:text-white transition-colors relative group"
              data-testid={`nav-${l.href.slice(1)}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-mh-orange group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone}`}
            className="hidden sm:flex items-center gap-2 text-sm text-white hover:text-mh-orange transition-colors"
            data-testid="nav-phone"
          >
            <Phone size={15} /> {COMPANY.phoneDisplay}
          </a>
          <button
            onClick={onQuote}
            className="hidden sm:block bg-mh-orange hover:bg-mh-orangeHover text-white text-sm font-semibold px-5 py-2.5 transition-colors"
            data-testid="nav-quote-btn"
          >
            Devis gratuit
          </button>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-mh-bg2 border-t border-mh-line"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="text-left text-lg text-white" data-testid={`mnav-${l.href.slice(1)}`}>
                  {l.label}
                </button>
              ))}
              <button onClick={() => { setOpen(false); onQuote(); }} className="mt-2 bg-mh-orange text-white font-semibold px-5 py-3" data-testid="mnav-quote-btn">
                Devis gratuit
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
